// 运行时配置
import { RunTimeLayoutConfig, RequestConfig } from '@umijs/max';
import { message, Modal, App } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { getUserInfo, logout } from '@/services/user';
import type { CurrentUser } from '@/services/types';
import { activateListCacheIdentity, resetListCacheIdentity } from '@/utils/listCacheIdentity';
import { initFeishuSDK } from '@/utils/feishu';
import { isMobile } from '@/utils/device';

// Another tab can replace the shared login token; reload before rendering cached business rows.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'token' || event.key === null) {
      resetListCacheIdentity();
      window.location.reload();
    }
  });
}

// 执行退出登录
const doLogout = async () => {
  try {
    await logout();
  } catch (error) {
    // 即使退出接口失败，也要清除本地登录状态
    console.error('退出登录失败:', error);
  }
  resetListCacheIdentity();
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// 退出登录确认
const handleLogout = () => {
  Modal.confirm({
    title: '退出登录',
    content: '确定要退出登录吗？',
    okText: '确定',
    cancelText: '取消',
    onOk: doLogout,
  });
};

// 全局初始化数据配置，用于 Layout 用户信息和权限初始化
// 更多信息见文档：https://umijs.org/docs/max/data-flow#%E5%85%A8%E5%B1%80%E5%88%9D%E5%A7%8B%E7%8A%B6%E6%80%81
export async function getInitialState(): Promise<{
  currentUser?: CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
}> {
  // 初始化飞书 SDK
  initFeishuSDK();

  const fetchUserInfo = async () => {
    const requestedToken = localStorage.getItem('token');
    try {
      const res = await getUserInfo();
      if (requestedToken !== localStorage.getItem('token')) return undefined;
      activateListCacheIdentity(res.data);
      return res.data;
    } catch (error) {
      if (requestedToken !== localStorage.getItem('token')) return undefined;
      resetListCacheIdentity();
      const { location } = history;
      if (location.pathname !== '/login' && !location.pathname.startsWith('/login/')) {
        history.push(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      }
    }
    return undefined;
  };

  // 如果是登录页面，检查是否已登录
  const { location } = history;
  if (location.pathname === '/login' || location.pathname.startsWith('/login/')) {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const currentUser = await fetchUserInfo();
        if (currentUser) {
          const { isMobile } = await import('@/utils/device');
          const urlParams = new URLSearchParams(location.search);
          const redirectUrl = urlParams.get('redirect');
          const defaultUrl = isMobile() ? '/m' : '/';
          history.replace(redirectUrl || defaultUrl);
          return { fetchUserInfo, currentUser };
        }
      } catch (e) {
        resetListCacheIdentity();
        localStorage.removeItem('token');
      }
    }
    return { fetchUserInfo };
  }

  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
  };
}

// ProLayout 配置
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    logo: '/logo.svg',
    menu: {
      locale: true,
    },
    // 添加 childrenRender 包裹 App 组件
    childrenRender: (children: React.ReactNode) => {
      return (
        <App>
          {children}
        </App>
      );
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: initialState?.currentUser?.name,
      size: 'small',
      render: (_: any, avatarChildren: React.ReactNode) => {
        return (
          <div
            onClick={handleLogout}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {avatarChildren}
            <LogoutOutlined style={{ fontSize: 14 }} title="退出登录" />
          </div>
        );
      },
    },
    actionsRender: () => [],
    footerRender: () => (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        小雨CRM ©{new Date().getFullYear()} 小雨科技
      </div>
    ),
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== '/login' && !location.pathname.startsWith('/login/')) {
        history.push(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }
      // 移动端访问 PC 路由时，跳转到对应的移动端页面（同路径加 /m 前缀），根路径走移动端落地网关按权限跳转。
      // 注意：/m 本身（落地网关）也是移动端路径，需排除，否则会被重写成 /m/m 导致白屏。
      if (initialState?.currentUser && location.pathname !== '/m' && !location.pathname.startsWith('/m/') && location.pathname !== '/login' && !location.pathname.startsWith('/login/')) {
        if (isMobile()) {
          const target = location.pathname === '/' ? '/m' : `/m${location.pathname}${location.search}`;
          history.replace(target);
        }
      }
    },
    menuHeaderRender: undefined,
  };
};

// request 配置

export const request: RequestConfig = {
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  requestInterceptors: [
    (config: any) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
  ],
  responseInterceptors: [
    (response: any) => {
      const { data } = response;
      // 检查业务状态码
      if (data && typeof data.code !== 'undefined' && data.code !== 200) {
        message.error(data?.message || '请求失败');
        if (data?.code === 401) {
          resetListCacheIdentity();
          localStorage.removeItem('token');
          const { location } = history;
          if (location.pathname !== '/login' && !location.pathname.startsWith('/login/')) {
            window.location.href = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
          }
        }
        return Promise.reject(data);
      }
      return response;
    },
  ],
  errorConfig: {
    errorHandler: (error: any) => {
      const { response } = error;
      if (response?.status === 401) {
        resetListCacheIdentity();
        localStorage.removeItem('token');
        if (!history.location.pathname.startsWith('/login')) window.location.href = '/login';
      }
      // TODO 这里先注释下，因为会和responseInterceptors的提示重复
      // if (response && response.status) {
      //   const errorText = codeMessage[response.status] || response.statusText;
      //   notification.error({
      //     message: `请求错误 ${response.status}`,
      //     description: errorText,
      //   });
      // } else if (!response) {
      //   notification.error({
      //     description: '网络异常，无法连接服务器',
      //     message: '网络异常',
      //   });
      // }
      return Promise.reject(error);
    },
  },
};
